/*
 * Copyright © 2020-2026 Ocean Galaxy Inc. All Rights Reserved.
 */
/**
 * 积分入口条（PointsEntryChip）文案逻辑 + CTA 映射。
 * 复用 PointsStatusCard 的身份归一化 / 状态计算 / 数值格式化。
 */
import {
  vipKindToThemeKey,
  computeUsageState,
  formatWan
} from '../PointsStatusCard/config'
import type { PointsThemeKey, UsageState } from '../PointsStatusCard/types'

export { vipKindToThemeKey, computeUsageState }

/** 单次填表约消耗的积分（用于「约可填写 N 次」与「不足 1 次」判断），默认 5 万 */
export const DEFAULT_ENTRY_PER_QUERY_COST = 50000

/** 预警 / 不足 1 次时的 CTA 文案（按身份：升级到下一级 / 超级增购） */
export const ENTRY_CTA: Record<PointsThemeKey, string> = {
  none: '升级会员 · 300 万积分 / 月起>',
  basic: '升级会员 · 600 万积分 / 月起>',
  advanced: '升级会员 · 3000 万积分 / 月>',
  super: '增购积分(永久有效)>'
}

/** 文案分段：text=灰常规 / num=橙高亮 / cta=蓝链接 */
export interface EntrySegment {
  text: string
  type: 'text' | 'num' | 'cta'
}

/** 紧凑数值（去掉 formatWan 的空格，对齐设计稿「58.54万」） */
function tight(n: number): string {
  return formatWan(n).replace(/\s+/g, '')
}

/** 按身份 + 状态生成入口条的分段文案 */
export function buildEntrySegments(
  themeKey: PointsThemeKey,
  usageState: UsageState,
  total: number,
  used: number,
  perQueryCost: number
): EntrySegment[] {
  const remaining = Math.max(0, total - used)
  const isMember = themeKey !== 'none'
  const cta: EntrySegment = { text: ENTRY_CTA[themeKey], type: 'cta' }

  // 满额 / 未使用
  if (usageState === 'unused') {
    if (!isMember) {
      return [
        { text: '赠送 ', type: 'text' },
        { text: tight(total), type: 'num' },
        { text: ' 积分，免费体验AI填表', type: 'text' }
      ]
    }
    return [
      { text: tight(total), type: 'num' },
      { text: ` 积分/月`, type: 'text' }
    ]
  }

  // 不足 1 次
  if (usageState === 'critical') {
    return [
      { text: '剩 ', type: 'text' },
      { text: tight(remaining), type: 'num' },
      {
        text: isMember ? ' ，' : ' ，',
        type: 'text'
      },
      cta
    ]
  }

  // 余额预警（已用 ≥ 90%）
  if (usageState === 'warning') {
    const z = total ? Math.round((used / total) * 100) : 0
    return [
      { text: '剩 ', type: 'text' },
      { text: tight(remaining), type: 'num' },
      { text: ' 积分，已用 ', type: 'text' },
      { text: `${z}%`, type: 'num' },
      { text: '，', type: 'text' },
      cta
    ]
  }

  // 一般使用
  return [
    { text: '剩 ', type: 'text' },
    { text: tight(remaining), type: 'num' },
    { text: ` 积分`, type: 'text' }
  ]
}
